import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { getObjectFilledWithValue } from '@/libs/helpers/utils';
import { DEFAULT_FIELD_ERROR } from '@/libs/validation/constants';
import { EMPTY_INPUT_VALUE } from '@/libs/constants';
import { FlightField } from './interface';
export const FLIGHTS_STATE_NAMESPACE = 'flights';
export const MAX_FLIGHTS_AMOUNT = 5;
const defaultFlight = () => ({
    _uuid: uuidv4(),
    airline: EMPTY_INPUT_VALUE,
    airlineCode: null,
    flightNumber: EMPTY_INPUT_VALUE,
    from: EMPTY_INPUT_VALUE,
    fromCode: null,
    to: EMPTY_INPUT_VALUE,
    toCode: null,
    dateFrom: EMPTY_INPUT_VALUE,
    dateTo: EMPTY_INPUT_VALUE,
    timeFrom: EMPTY_INPUT_VALUE,
    timeTo: EMPTY_INPUT_VALUE,
    fieldsErrors: getObjectFilledWithValue(FlightField, DEFAULT_FIELD_ERROR),
    fieldsDirty: getObjectFilledWithValue(FlightField, false),
});
const getFlightDepartureTimestamp = (flight) => {
    const [day, month, year] = flight.dateFrom.split('.').map(Number);
    const [hours, minutes] = flight.timeFrom.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes).getTime();
};
const initialState = {
    flights: [defaultFlight()],
    isFlightsValidated: false,
    isFlightsDirty: false,
    stepError: null,
    stepErrorType: null,
};
const flightsSlice = createSlice({
    name: FLIGHTS_STATE_NAMESPACE,
    initialState,
    reducers: {
        addFlight: (state) => {
            if (state.flights.length < MAX_FLIGHTS_AMOUNT) {
                state.flights.push(defaultFlight());
            }
        },
        removeFlight: (state, action) => {
            if (state.flights.length > 1) {
                state.flights = state.flights.filter((flight) => flight._uuid !== action.payload);
            }
        },
        moveFlight: (state, action) => {
            const fromIndex = state.flights.findIndex((flight) => flight._uuid === action.payload._uuid);
            if (fromIndex === -1) {
                return;
            }
            const toIndex = Math.max(0, Math.min(action.payload.toIndex, state.flights.length - 1));
            if (fromIndex === toIndex) {
                return;
            }
            const [flight] = state.flights.splice(fromIndex, 1);
            state.flights.splice(toIndex, 0, flight);
        },
        sortFlightsByDeparture: (state) => {
            state.flights.sort((firstFlight, secondFlight) => getFlightDepartureTimestamp(firstFlight) -
                getFlightDepartureTimestamp(secondFlight));
            state.stepError = null;
            state.stepErrorType = null;
            state.isFlightsValidated = false;
        },
        _setFlightField: (state, action) => {
            const flight = state.flights.find((f) => f._uuid === action.payload._uuid);
            if (flight) {
                flight[action.payload.field] = action.payload.value;
            }
        },
        _setFlightCode: (state, action) => {
            const flight = state.flights.find((f) => f._uuid === action.payload._uuid);
            if (flight) {
                flight[action.payload.field] = action.payload.code;
            }
        },
        _setFlightFieldError: (state, action) => {
            const flight = state.flights.find((f) => f._uuid === action.payload._uuid);
            if (flight) {
                flight.fieldsErrors[action.payload.field] =
                    action.payload.error;
            }
        },
        setFlightFieldDirty: (state, action) => {
            const flight = state.flights.find((f) => f._uuid === action.payload._uuid);
            if (flight) {
                flight.fieldsDirty[action.payload.field] = true;
            }
        },
        _setFlightsValidated: (state, action) => {
            state.isFlightsValidated = action.payload;
        },
        _setStepError: (state, action) => {
            state.stepError = action.payload.error;
            state.stepErrorType = action.payload.error
                ? (action.payload.type ?? null)
                : null;
        },
        setFlightsDirty: (state, action) => {
            state.isFlightsDirty = action.payload ?? true;
        },
        setAllFlightsFieldsDirty: (state) => {
            state.flights.forEach((flight) => {
                Object.keys(flight.fieldsDirty).forEach((field) => {
                    flight.fieldsDirty[field] = true;
                });
            });
        },
    },
});
export const flightsActions = flightsSlice.actions;
export const flightsReducer = flightsSlice.reducer;
